import { apiFetch } from '@/lib/apiFetch';
import { useState, useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  Mail, MapPin, Send, MessageCircle, Clock,
  CheckCircle, Sparkles, ChevronDown, ChevronUp,
  HeadphonesIcon, GraduationCap, Briefcase, AlertCircle,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/* ── Scroll reveal ── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ── Data ── */
const contactChannels = [
  {
    icon: Mail,
    label: "Email Support",
    value: "support@bookmycoaching.com",
    sub: "Responses within 24 hours",
    gradient: "from-secondary to-[hsl(180,50%,42%)]",
    href: "mailto:support@bookmycoaching.com",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "Sector 132, Noida",
    sub: "Urbtech Trade Centre, Uttar Pradesh 201304",
    gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]",
    href: "https://maps.google.com",
  },
];

const topics = [
  { icon: HeadphonesIcon, label: "General Support", value: "general" },
  { icon: GraduationCap, label: "Student Enquiry", value: "student" },
  { icon: Briefcase, label: "Teacher Application", value: "coach" },
  { icon: AlertCircle, label: "Report an Issue", value: "issue" },
  { icon: Mail, label: "Partnership / Press", value: "partnership" },
  { icon: MessageCircle, label: "Other", value: "other" },
];

const faqs = [
  {
    q: "How quickly will I receive a response?",
    a: "Our support team aims to respond to all enquiries within 24 hours on business days. For urgent matters, our support team is available Monday to Friday, 9 am – 7 pm IST.",
  },
  {
    q: "I'm a teacher — who should I contact for onboarding help?",
    a: "Select 'Teacher Application' as your topic when filling the form, or email us directly at support@bookmycoaching.com. Our Teacher Success team will reach out within 1 business day.",
  },
  {
    q: "How do I report a problem with a session or a teacher?",
    a: "Select 'Report an Issue' in the contact form and describe the situation. You can also raise a formal dispute from the Session detail page in your dashboard, which triggers our mediation process.",
  },
  {
    q: "Can I request a demo for my institution or school?",
    a: "Absolutely. Select 'Partnership / Press' as your topic and mention that you'd like a demo. One of our institutional partnerships team members will be in touch to schedule a call.",
  },
];

export default function ContactPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const formRef = useReveal<HTMLDivElement>();
  const { pathname } = useLocation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTopic, setActiveTopic] = useState("general");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiFetch('/api/contact/v1/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, topic: activeTopic }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSent(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const title = 'Contact Us | BookMyCoaching';
  const description = "Get in touch with the BookMyCoaching team. We're here to help students and tutors.";
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>
        {/* ── Hero ── */}
        <section className="relative pt-28 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,55%,15%)] via-[hsl(210,55%,18%)] to-[hsl(190,45%,14%)]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-secondary/12 rounded-full blur-[150px]" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-[hsl(260,60%,50%)]/8 rounded-full blur-[120px]" />
          </div>
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full">
              <defs>
                <pattern id="contact-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#contact-grid)" />
            </svg>
          </div>

          <div ref={heroRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-5 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20">
              <Sparkles className="w-3 h-3" />
              We're here to help
            </span>
            <h1 className="font-sans text-4xl sm:text-5xl text-white mb-5 leading-tight">
              Get in Touch
            </h1>
            <p className="text-white/60 max-w-xl mx-auto leading-relaxed">
              Whether you have a question about a session, need help with your account, or want to explore a partnership — our team is ready.
            </p>

            {/* Response time pill */}
            <div className="inline-flex items-center gap-2 mt-7 px-4 py-2 rounded-full bg-white/8 border border-white/12 text-white/60 text-xs backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
              </span>
              Typical response time: <span className="text-white/80 font-semibold ml-1">under 24 hours</span>
            </div>
          </div>
        </section>

        {/* ── Contact channels ── */}
        <section className="py-10 bg-muted/30 border-b border-border/40">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {contactChannels.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-border/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group"
                >
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br shadow-md",
                    c.gradient
                  )}>
                    <c.icon className="w-4.5 h-4.5 text-white w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">{c.label}</p>
                    <p className="text-sm font-semibold text-foreground group-hover:text-secondary transition-colors">{c.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── Form + Info ── */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div ref={formRef} className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto reveal">

              {/* Left — form */}
              <div className="lg:col-span-3">
                <div className="mb-8">
                  <h2 className="font-sans text-2xl sm:text-3xl text-foreground mb-2">Send us a message</h2>
                  <p className="text-muted-foreground text-sm">Fill in the form below and we'll be in touch shortly.</p>
                </div>

                {sent ? (
                  <div className="rounded-2xl border border-secondary/30 bg-secondary/5 p-10 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle className="w-7 h-7 text-secondary" />
                    </div>
                    <h3 className="font-sans text-xl text-foreground mb-2">Message received!</h3>
                    <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
                      Thanks for reaching out. One of our team members will reply to <span className="font-medium text-foreground">{formData.email || "your email"}</span> within 24 hours.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => { setSent(false); setFormData({ name: "", email: "", message: "" }); }}
                    >
                      Send another message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Topic selector */}
                    <div>
                      <Label className="text-sm font-medium text-foreground mb-3 block">What's this about?</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {topics.map((t) => {
                          const Icon = t.icon;
                          return (
                            <button
                              key={t.value}
                              type="button"
                              onClick={() => setActiveTopic(t.value)}
                              className={cn(
                                "flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium border transition-all duration-200 text-left",
                                activeTopic === t.value
                                  ? "bg-secondary/10 border-secondary/40 text-secondary"
                                  : "bg-card border-border/40 text-muted-foreground hover:border-border hover:text-foreground"
                              )}
                            >
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              <span className="text-xs leading-tight">{t.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Name + Email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Arjun Mehta"
                          required
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="you@example.com"
                          required
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Your message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us how we can help you..."
                        rows={6}
                        required
                        className="rounded-xl resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      className="rounded-full px-8 bg-gradient-to-r from-secondary to-[hsl(180,50%,42%)] text-white shadow-lg shadow-secondary/25 hover:-translate-y-0.5 hover:shadow-xl active:brightness-95 transition-all"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          Sending…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>

              {/* Right — info */}
              <div className="lg:col-span-2 flex flex-col gap-6">

                {/* Hours card */}
                <div className="rounded-2xl border border-border/40 bg-card p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-secondary" />
                    </div>
                    <h3 className="font-semibold text-foreground">Support Hours</h3>
                  </div>
                  <div className="flex flex-col gap-3">
                    {[
                      { day: "Monday – Friday", hours: "9:00 am – 7:00 pm IST" },
                      { day: "Saturday", hours: "10:00 am – 4:00 pm IST" },
                      { day: "Sunday", hours: "Closed" },
                    ].map((row, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-2 border-b border-border/40 last:border-0">
                        <span className="text-muted-foreground">{row.day}</span>
                        <span className={cn("font-medium", row.hours === "Closed" ? "text-muted-foreground" : "text-foreground")}>{row.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dedicated emails */}
                <div className="rounded-2xl border border-border/40 bg-card p-6">
                  <h3 className="font-semibold text-foreground mb-4">Dedicated inboxes</h3>
                  <div className="flex flex-col gap-3">
                    {[
                      { label: "General support", email: "support@bookmycoaching.com" },
                    ].map((row, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 py-2 border-b border-border/40 last:border-0">
                        <span className="text-xs text-muted-foreground">{row.label}</span>
                        <a href={`mailto:${row.email}`} className="text-xs font-medium text-secondary hover:underline underline-offset-4 truncate">{row.email}</a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live chat nudge */}
                <div className="rounded-2xl bg-gradient-to-br from-[hsl(165,55%,18%)] to-[hsl(210,55%,22%)] border border-secondary/20 p-6 text-center">
                  <MessageCircle className="w-8 h-8 text-secondary mx-auto mb-3" />
                  <p className="font-semibold text-white text-sm mb-1">Prefer live chat?</p>
                  <p className="text-xs text-white/55 mb-4 leading-relaxed">Use the chat widget in the bottom-right corner of the screen for real-time help during support hours.</p>
                  <span className="inline-flex items-center gap-1.5 text-xs text-secondary font-medium">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
                    </span>
                    Online now
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-20 bg-muted/30 border-t border-border/40">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-secondary mb-4 px-3 py-1 rounded-full bg-secondary/10">
                <Sparkles className="w-3 h-3" />
                Quick answers
              </span>
              <h2 className="font-sans text-3xl text-foreground mb-3">Frequently Asked Questions</h2>
              <p className="text-muted-foreground text-sm">Can't find what you're looking for? Use the form above.</p>
            </div>

            <div className="flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-2xl border border-border/40 bg-card overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-sm font-semibold text-foreground">{faq.q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 text-secondary flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border/40 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
